<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row flex-wrap justify-space-between align-center elevation-3 mb-3">
      <span>
        Score Setup - {{ title }}
      </span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <v-btn
          color="secondary"
          rounded
          small
          :loading="loading"
          :disabled="!valid || totalWeight !== 100"
          @click="onShowCopyDialog()">
          Copy
        </v-btn>
        <v-btn
          color="error"
          rounded
          small
          outlined
          :loading="loading"
          :disabled="!isEdit"
          @click="onReset()">
          Reset
        </v-btn>
        <v-btn
          color="success"
          rounded
          small
          :loading="loading"
          :disabled="!isEdit || !valid || totalWeight !== 100"
          @click="onConfirmSave()">
          Save
        </v-btn>
      </div>
    </div>

    <v-row class="px-8">
      <v-col
        cols="12"
        class="card-container elevation-3">
        <div class="d-flex flex-row flex-wrap justify-space-between">
          <div>
            <h2>
              Evaluation Topics
            </h2>
            <p class="grey--text text-caption">
              Enter your target and weight to calculate an overall score that use for calculate PMS score.
            </p>
          </div>
          <h3>
            <span>
              Total Weight (%):
            </span>
            <span :class="totalWeight === 100 ? 'green--text' : 'red--text'">
              {{ totalWeight | showNumberFormat }}
            </span>
            <span>
              / 100
            </span>
          </h3>
        </div>
        <v-form
          ref="setup-form"
          v-model="valid"
          @submit.prevent="onConfirmSave()">
          <v-data-table
            :headers="headers"
            :items="formData.topics"
            :items-per-page="-1"
            :loading="loading"
            hide-default-footer>
            <template #[`item.available`]="{ item }">
              <div class="d-flex justify-center align-center pl-4">
                <v-switch
                  v-model="item.available"
                  class="mt-0"
                  color="secondary"
                  inset
                  dense
                  hide-details
                  :disabled="loading || item.disabled" />
              </div>
            </template>
            <template #[`item.name`]="{ item }">
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <span
                    class="text-decoration-underline"
                    style="cursor: pointer;"
                    v-bind="attrs"
                    v-on="on">
                    {{ item.name }}
                  </span>
                </template>
                <div style="white-space: pre-line;">
                  {{ item.formula }}
                </div>
              </v-tooltip>
            </template>
            <template #[`item.target`]="{ item }">
              <v-text-field
                v-if="item.topicType !== 'low_review'"
                v-model.number="item.target"
                type="number"
                :rules="item.disabled || !item.available ? [] : numberRules"
                :readonly="loading"
                :suffix="item.unit"
                label="Target"
                color="secondary"
                dense
                outlined
                hide-details
                :disabled="item.disabled || !item.available" />
              <span v-else>
                ไม่ต้องระบุ
              </span>
            </template>
            <template #[`item.weight`]="{ item }">
              <v-text-field
                v-model.number="item.weight"
                type="number"
                :rules="item.disabled || !item.available ? [] : numberRules"
                :readonly="loading"
                suffix="%"
                label="Weight"
                color="secondary"
                dense
                outlined
                hide-details
                :disabled="item.disabled || !item.available" />
            </template>
          </v-data-table>
        </v-form>
      </v-col>
    </v-row>
    <v-dialog
      v-model="dialog"
      max-width="500px"
      persistent>
      <v-card>
        <v-card-title>
          <h3>
            Copy Score Setup
          </h3>
          <span class="grey--text text-caption">
            Please select target warehouse to paste this setting.
          </span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form
              ref="copy-form"
              @submit.prevent="onConfirmCopy()">
              <v-row>
                <v-col cols="12">
                  <WarehouseSelect
                    v-model="targetWarehouse"
                    :items="warehouses"
                    item-text="name"
                    item-value="id"
                    :menu-props="{ offsetY: true }"
                    :rules="requiredRules"
                    label="Warehouse"
                    color="secondary"
                    outlined
                    dense
                    hide-details
                    return-object />
                </v-col>
                <v-col cols="12">
                  <DatePicker
                    v-model="targetDate"
                    type="month"
                    :min="currentMonth"
                    color="secondary"
                    outlined />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="secondary"
            text
            :disabled="loading"
            @click="onClose()">
            Cancel
          </v-btn>
          <v-btn
            :loading="loading"
            color="success"
            text
            @click="onConfirmCopy()">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import PerformanceScoresProvider from '@/resources/PerformanceScoresProvider'
import EvaluationTopics from '@/assets/json/EvaluationTopics.json'
import WarehouseProvider from '@/resources/WarehouseProvider'
import WarehouseSelect from '@/components/WarehouseSelect.vue'
import DatePicker from '@/components/DatePicker.vue'

const PerformanceScoresService = new PerformanceScoresProvider()
const WarehouseService = new WarehouseProvider()

export default {
  components: {
    WarehouseSelect,
    DatePicker
  },
  data () {
    return {
      currentMonth: this.$dayjs().format('YYYY-MM'),
      query: {
        month: this.$dayjs().month() + 1,
        year: this.$dayjs().year(),
        warehouseId: null
      },
      mainTopics: EvaluationTopics,
      headers: [
        {
          text: 'Available',
          value: 'available',
          sortable: false,
          align: 'center',
          width: '120px'
        },
        {
          text: 'Topic',
          value: 'name',
          sortable: false
        },
        // {
        //   text: 'Condition Type',
        //   value: 'conditionType',
        //   sortable: false,
        //   align: 'center',
        //   cellClass: 'capitalize'
        // },
        {
          text: 'Target',
          value: 'target',
          sortable: false,
          align: 'center',
          width: '200px'
        },
        {
          text: 'Weight',
          value: 'weight',
          sortable: false,
          align: 'center',
          width: '200px'
        }
      ],
      formData: {
        id: null,
        month: this.$dayjs().month() + 1,
        year: this.$dayjs().year(),
        warehouse: null,
        topics: [],
        status: 'inactive'
      },
      loading: false,
      warehouse: null,
      memoFormData: null,
      operators: {
        greater_than_or_equal: 'มากกว่า หรือเท่ากับ',
        less_than_or_equal: 'น้อยกว่า หรือเท่ากับ',
        greater_than: 'มากกว่า',
        less_than: 'น้อยกว่า',
        equal: 'เท่ากับ'
      },
      requiredRules: [
        (v) => !!v || 'This field is required!'
      ],
      numberRules: [
        (v) => /^[+-]?([0-9]*[.])?[0-9]+$/.test(v) || 'This field is invalid.',
        (v) => v >= 0 || 'This field must be positive number.'
      ],
      dialog: false,
      targetWarehouse: null,
      targetDate: this.$dayjs().format('YYYY-MM'),
      valid: false
    }
  },
  computed: {
    warehouses () {
      return this.$store.getters['Store/warehouse']
        ?.filter((warehouse) => Number(warehouse.id) !== 0
          && warehouse.type === 'store'
          && warehouse.status === 'active')
        ?.sort((a, b) => a.name.localeCompare(b.name)) || []
    },
    title () {
      return `${this.warehouse ? this.warehouse.name : 'Unknown'} (${this.query.month}/${this.query.year})`
    },
    isEdit () {
      return JSON.stringify(this.formData) !== JSON.stringify(this.memoFormData)
    },
    totalWeight () {
      return this.formData.topics.reduce((acc, topic) => {
        if (!topic?.available) {
          return acc
        }

        return acc + (topic?.weight || 0)
      }, 0)
    }
  },
  async mounted () {
    this.query.warehouseId = this.$route.params.id
    this.query.year = Number(this.$route.query.date.split('-')[0])
    this.query.month = Number(this.$route.query.date.split('-')[1])

    await this.getWarehouse()
    this.getItem()
  },
  beforeRouteLeave (to, from, next) {
    if (this.isEdit) {
      // eslint-disable-next-line no-alert
      const confirmLeave = window.confirm('You have unsaved changes.\nDo you want to stay and continue editing, or leave without saving?')

      if (confirmLeave) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },
  methods: {
    async getWarehouse () {
      try {
        this.loading = true

        const { data } = await WarehouseService.getWarehouseById(this.query.warehouseId)

        this.warehouse = data
        this.formData.warehouse = data
      } catch (error) {
        console.error('getWarehouse', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getItem () {
      try {
        this.loading = true

        const { data } = await PerformanceScoresService.getItemByWarehouseId(this.query.warehouseId, this.query.month, this.query.year)

        if (data) {
          this.formData = {
            ...data,
            topics: this.mainTopics.map((topic) => {
              const found = data.topics.find((v) => v.topicType === topic.id)

              return {
                name: topic.name,
                topicType: topic.id,
                operator: found?.operator || topic.operator,
                conditionType: found?.conditionType || topic.conditionType,
                formula: topic.formula,
                unit: topic.unit,
                target: found?.target ?? null,
                weight: found?.weight ?? null,
                available: found?.available || false,
                disabled: topic?.disabled || false
              }
            })
          }
        } else {
          this.formData = {
            ...this.formData,
            month: this.query.month,
            year: this.query.year,
            topics: this.mainTopics.map((topic) => ({
              name: topic.name,
              topicType: topic.id,
              operator: topic.operator,
              conditionType: topic.conditionType,
              formula: topic.formula,
              unit: topic.unit,
              target: null,
              weight: null,
              available: false,
              disabled: topic?.disabled || false
            }))
          }
        }

        this.memoFormData = JSON.parse(JSON.stringify(this.formData))
      } catch (error) {
        console.error('getItem', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    getMaxPoint (topicIndex) {
      return Math.max(...this.formData.topics[topicIndex].conditions.map((v) => v.point))
    },
    getMinPoint (topicIndex) {
      return Math.min(...this.formData.topics[topicIndex].conditions.map((v) => v.point))
    },
    onAddCondition (topicIndex) {
      this.formData.topics[topicIndex].conditions.push({
        value: 0,
        point: 0
      })
    },
    removeCondition (topicIndex, index) {
      this.formData.topics[topicIndex].conditions.splice(index, 1)
    },
    async onConfirmSave () {
      const isValid = await this.$refs['setup-form'].validate()

      if (!isValid) {
        return
      }

      this.$store.dispatch('Components/setModal', {
        value: true,
        title: 'Save Item',
        message: 'Do you want to save this progress?',
        confirmText: 'Save',
        confirmType: 'success',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.onSave()
      })
    },
    async onSave () {
      try {
        this.loading = true

        const isValid = await this.$refs['setup-form'].validate()

        if (!isValid) {
          return
        }

        const tmpFormData = {
          ...this.formData,
          topics: this.formData.topics.map((topic) => {
            const tmpTopic = { ...topic }

            if (tmpTopic.topicType === 'low_review') {
              tmpTopic.weight = tmpTopic.weight || 0
              tmpTopic.target = tmpTopic.weight || 0
            } else if (tmpTopic?.disabled || !tmpTopic.available) {
              tmpTopic.target = tmpTopic.target || 0
              tmpTopic.weight = tmpTopic.weight || 0
            }

            return tmpTopic
          })
        }

        await PerformanceScoresService.createItem(tmpFormData)

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Save successfully',
          type: 'success'
        })

        this.getItem()
      } catch (error) {
        console.error('onSave', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    onReset () {
      this.formData = JSON.parse(JSON.stringify(this.memoFormData))
    },
    onShowCopyDialog () {
      this.targetWarehouse = null
      this.targetDate = this.$dayjs().format('YYYY-MM')
      this.dialog = true
    },
    async onConfirmCopy () {
      const isValid = await this.$refs['copy-form'].validate()

      if (!isValid) {
        return
      }

      this.$store.dispatch('Components/setModal', {
        value: true,
        title: 'Copy Item',
        message: 'Are you want to copy this setting?',
        confirmText: 'Copy',
        confirmType: 'success',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.onCopy()
      })
    },
    async onCopy () {
      try {
        this.loading = true

        const year = Number(this.targetDate.split('-')[0])
        const month = Number(this.targetDate.split('-')[1])

        await PerformanceScoresService.createItem({
          ...this.formData,
          warehouse: {
            id: this.targetWarehouse.id,
            name: this.targetWarehouse.name,
            code: this.targetWarehouse.code
          },
          month,
          year
        })

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Copy setup successfully.',
          type: 'success'
        })

        this.onClose()
        this.getItem()
      } catch (error) {
        console.error('onCopy', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    onClose () {
      this.dialog = false
      this.targetWarehouse = null
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
</style>
